import { getTableA, getTableB } from "./tables";
import { addHours } from "./timeUtils";

/**
 * Convert an "HH:MM" local time string + a UTC offset into a UTC Date.
 *
 * Steps:
 *  1. Place the HH:MM on the same calendar date as dutyStart (in UTC).
 *  2. Subtract the airport's UTC offset to get true UTC.
 *  3. If the result is before dutyStart (UTC), advance one day — handles
 *     overnight legs and wraparound at midnight.
 *
 * @param {Date}   dutyStart       - Report time as a UTC Date
 * @param {string} localTimeStr    - "HH:MM" local time at the airport
 * @param {number} utcOffsetHours  - Airport's UTC offset (e.g. -8 for PST)
 * @returns {Date} UTC Date
 */
export function resolveTimeUTC(dutyStart, localTimeStr, utcOffsetHours = 0) {
  const [h, m] = localTimeStr.split(":").map(Number);

  // Find the LOCAL calendar date that duty start falls on, using the
  // airport's UTC offset. This is critical for overnight reports:
  // e.g. report 20:00 UTC-7 = 03:00 UTC next day — we must place
  // leg times on the local date (2026-03-25), not the UTC date (2026-03-26).
  const dutyLocalMs  = dutyStart.getTime() + utcOffsetHours * 3_600_000;
  const dutyLocalDate = new Date(dutyLocalMs);
  const year  = dutyLocalDate.getUTCFullYear();
  const month = dutyLocalDate.getUTCMonth();
  const day   = dutyLocalDate.getUTCDate();

  // Local midnight of that date converted to UTC, then place HH:MM on it
  const localMidnightUTC = Date.UTC(year, month, day) - utcOffsetHours * 3_600_000;
  const candidate = new Date(localMidnightUTC + h * 3_600_000 + m * 60_000);

  // If the result is still before duty start (e.g. very early arr on a
  // cross-midnight leg), advance one calendar day
  if (candidate < dutyStart) {
    return new Date(candidate.getTime() + 86_400_000);
  }
  return candidate;
}

/**
 * Format a UTC Date as local "HH:MM" at a given UTC offset.
 * Used by the UI to display times in each airport's local zone.
 */
export function utcToLocal(utcDate, utcOffsetHours = 0) {
  if (!utcDate || isNaN(utcDate)) return "--";
  const local = new Date(utcDate.getTime() + utcOffsetHours * 3_600_000);
  const h = String(local.getUTCHours()).padStart(2, "0");
  const m = String(local.getUTCMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

/**
 * evaluateFDP
 *
 * segments: Array of {
 *   dep:        "HH:MM"  — local time at depAirport
 *   arr:        "HH:MM"  — local time at arrAirport
 *   depAirport: { utc_offset_hours, icao_code, iata_code, name, time_zone } | null
 *   arrAirport: { utc_offset_hours, icao_code, iata_code, name, time_zone } | null
 * }
 *
 * acclimatedOffset: number | null
 *   The pilot's acclimated timezone UTC offset (hours).  Per FAR 117, Table B
 *   and Table A lookups must use the crew's ACCLIMATED timezone, not the
 *   departure airport's timezone.  A crew member is acclimated to a timezone
 *   after spending ≥ 72 hours there.  If null/undefined, falls back to the
 *   first departure airport's offset (legacy behaviour).
 */
export function evaluateFDP({ dutyStart, segments, pilots = 2, acclimatedOffset = null }) {
  const legs = segments.length;

  // Use acclimated offset for Table lookups if provided; otherwise fall back
  // to the first departure airport's offset (or 0 if no airport data).
  const baseOffset = acclimatedOffset ?? segments[0]?.depAirport?.utc_offset_hours ?? 0;
  const dutyStartLocal = new Date(dutyStart.getTime() + baseOffset * 3_600_000);

  const fdpLimit    = getTableB(dutyStartLocal, legs);
  const dutyEndLimit = addHours(dutyStart, fdpLimit);   // still tracked in UTC
  const flightLimit  = getTableA(dutyStartLocal, pilots);

  let totalFlightTime = 0;

  const timeline = segments.map((seg, i) => {
    const depOffset = seg.depAirport?.utc_offset_hours ?? 0;
    const arrOffset = seg.arrAirport?.utc_offset_hours ?? 0;

    const depUTC = resolveTimeUTC(dutyStart, seg.dep, depOffset);
    let   arrUTC = resolveTimeUTC(dutyStart, seg.arr, arrOffset);

    // Arrival must be after departure (protects against same-offset overnight)
    if (arrUTC <= depUTC) arrUTC = new Date(arrUTC.getTime() + 86_400_000);

    const blockMin = Math.round((arrUTC - depUTC) / 60_000);
    totalFlightTime += blockMin;

    return {
      leg: i + 1,
      depUTC,
      arrUTC,
      // Convenience: local display strings for each airport
      depLocal: utcToLocal(depUTC, depOffset),
      arrLocal: utcToLocal(arrUTC, arrOffset),
      depOffset,
      arrOffset,
      blockMin,
      legal: arrUTC <= dutyEndLimit,
    };
  });

  // Latest takeoff: FDP end minus the last leg's block time
  const lastLeg     = timeline[timeline.length - 1];
  const latestTakeoff = new Date(dutyEndLimit.getTime() - lastLeg.blockMin * 60_000);

  const overallLegal =
    timeline[timeline.length - 1].arrUTC <= dutyEndLimit &&
    totalFlightTime / 60 <= flightLimit;

  return {
    limits: { fdpLimit, dutyEndLimit, flightLimit },
    timeline,
    totalFlightTime,
    latestTakeoff,
    overallLegal,
    baseOffset,           // expose so UI can display dutyStart in local time
  };
}